import { yup } from '@framework/validation/initYup.ts';
import { BooleanConditionSchema } from '../../../../inputs/expressions-editor/conditions/BooleanConditionSchema.ts';
import { type IBooleanCondition } from '../../../../inputs/expressions-editor/conditions/IBooleanCondition.tsx';
import { cachedStripHtml } from '../../../../validator/utils/cachedStripHtml.ts';
import {
  type IConditionData,
  WidgetConditionManager,
} from '../../../../validator/WidgetConditionManager.ts';
import { evaluateExpression } from '../../../expressions/evaluate/evaluateExpression.ts';
import { createEvaluationVariables } from '../../../expressions/evaluate/variables/IEvaluationVariables.ts';
import { RuleType } from '../../IRule.ts';
import { type IRuleType } from '../../IRuleType.ts';
import { CustomWarningsRuleComponent } from './CustomWarningsRuleComponent.tsx';
import { CustomWarningRuleType } from './ICustomWarningRule.ts';

const CustomWarningsRuleSchema: yup.ObjectSchema<
  ICustomWarningRuleType | null | undefined
> = yup
  .object({
    rules: yup
      .array()
      .of(
        yup
          .object({
            type: yup
              .string()
              .oneOfEnum(CustomWarningRuleType)
              .required()
              .label('Livello'),
            message: yup.string().required().label('Messaggio'),
            condition: BooleanConditionSchema, // TODO -> al momento ritorna un Lazy che non ha la funzione ".label" // .label('Condizione')
          })
          .required(),
      )
      .notRequired()
      .nullable(),
  })
  .notRequired()
  .noUnknown()
  .nullable();

export type ICustomWarningRuleType =
  | {
      rules?: ICustomWarning[] | null;
    }
  | null
  | undefined;

export interface ICustomWarning {
  type: CustomWarningRuleType;
  message: string;
  condition: IBooleanCondition | null;
}

/**
 * Dati pre-calcolati di una singola regola custom (warning o errore)
 * con condizione dinamica. Usati per consolidare tutte le regole in un
 * singolo `parentWhen`, riducendo da N clone a 1 durante resolve/cast.
 */
interface IDynamicRuleData {
  type: CustomWarningRuleType;
  strippedMessage: string;
  conditionData: NonNullable<IConditionData>;
}

export const CustomWarningsRule: IRuleType<ICustomWarningRuleType> = {
  type: RuleType.CustomWarnings,
  name: 'Accertamenti personalizzati',
  configComponent: CustomWarningsRuleComponent,
  configValidator: CustomWarningsRuleSchema,
  validator: (schema, rule) => {
    if (!rule?.rules) return schema;

    // Raccogliamo separatamente le regole statiche (che non richiedono
    // parentWhen) e quelle dinamiche (che consolideremo in un singolo
    // parentWhen per ridurre i clone durante resolve/cast).
    const dynamicRules: IDynamicRuleData[] = [];

    rule.rules.forEach(rule => {
      const conditionData = WidgetConditionManager.getConditionData(
        rule.condition,
      );

      if (!conditionData) return;

      // Pre-compute il messaggio a build time per evitare stripHtml a runtime
      const strippedMessage = cachedStripHtml(rule.message);

      // Le condizioni statiche non richiedono parentWhen: le applichiamo subito
      if (conditionData.condition.type === 'static') {
        if (rule.type === CustomWarningRuleType.Error) {
          schema = getStaticErrorTest(schema, strippedMessage, conditionData);
        }
        if (rule.type === CustomWarningRuleType.Warning) {
          schema = getStaticWarningTest(schema, strippedMessage, conditionData);
        }
        return;
      }

      dynamicRules.push({ type: rule.type, strippedMessage, conditionData });
    });

    // Consolidamento: un singolo parentWhen per TUTTE le regole dinamiche
    if (dynamicRules.length > 0) {
      schema = applyDynamicRulesConsolidated(schema, dynamicRules);
    }

    return schema;
  },
};

// --- Gestione condizioni STATICHE (non richiedono parentWhen) ---

/**
 * Ritorna lo schema con il test sull'errore custom per condizioni statiche.
 */
function getStaticErrorTest(
  schema: yup.AnySchema,
  strippedMessage: string,
  conditionData: NonNullable<IConditionData>,
) {
  return (conditionData.condition as { value: boolean }).value
    ? schema.test({
        name: 'custom-warning',
        message: strippedMessage,
        test: () => false,
      })
    : schema;
}

/**
 * Ritorna lo schema con il warning custom per condizioni statiche.
 */
function getStaticWarningTest(
  schema: yup.AnySchema,
  strippedMessage: string,
  conditionData: NonNullable<IConditionData>,
) {
  return (conditionData.condition as { value: boolean }).value
    ? schema.warningMeta(strippedMessage)
    : schema;
}

// --- Gestione condizioni DINAMICHE (consolidate in un singolo parentWhen) ---

/**
 * Consolida tutte le regole dinamiche in un singolo `parentWhen`.
 *
 * Prima di questa ottimizzazione, ogni regola aggiungeva il proprio
 * `parentWhen`, causando N `resolve()` → N `clone()` durante cast/validate.
 * Con il consolidamento: 1 solo `parentWhen` → 1 solo `clone()`.
 */
function applyDynamicRulesConsolidated(
  schema: yup.AnySchema,
  dynamicRules: IDynamicRuleData[],
) {
  // Uniamo tutti i path di dipendenza (deduplicati) e costruiamo
  // una mappa path→indice per poter estrarre i valori corretti per
  // ogni regola dal singolo array `values` del parentWhen.
  const allPaths = [
    ...new Set(dynamicRules.flatMap(r => r.conditionData.paths)),
  ];
  const pathIndex = new Map<string, number>();
  for (let i = 0; i < allPaths.length; i++) {
    pathIndex.set(allPaths[i], i);
  }

  return schema.parentWhen(allPaths, (values, schema: any) => {
    return schema.withMutation((s: any) => {
      for (const rule of dynamicRules) {
        const { conditionData, strippedMessage, type } = rule;
        const { condition, references, paths } = conditionData;

        if (condition.type !== 'expression') continue;

        // Estraiamo solo i valori corrispondenti ai path di questa regola
        const ruleValues = paths.map(p => values[pathIndex.get(p) ?? 0]);
        const variables = createEvaluationVariables(references, ruleValues);
        const conditionResult = evaluateExpression(condition.expression, {
          variables,
        });

        if (type === CustomWarningRuleType.Error) {
          s.test({
            name: 'custom-warning',
            message: strippedMessage,
            test: (value: any, context: any) => {
              if (context.schema?.spec?.strip) return true;
              return !conditionResult;
            },
          });
          s.meta({
            customErrorDeps: [...paths, ...(s.meta().customErrorDeps ?? [])],
          });
        }

        if (type === CustomWarningRuleType.Warning && conditionResult) {
          s.warningMeta(strippedMessage);
        }
      }
      return s;
    });
  });
}
